import dayjs from 'dayjs';
import orderBy from 'lodash/orderBy';
import * as React from 'react';
import { AutoSizer } from 'react-virtualized';
import {
  CartesianGrid,
  Legend,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ISessionMean } from '../../../data/providers/reports/weightReportProvider';
import { ISize } from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { legendFormatter } from '../../shared/graphs/legendFormatter';
import { EPdfMode } from '../../shared/pdf/Pdf';
import { WeightTooltip } from './WeightTooltip';

interface IProps {
  sessions: ISessionMean[];
  pdfMode?: EPdfMode;
}

interface IDataPoint {
  Date: number;
  Weight: number;
  units?: string;
}

export const WeightGraph = (props: IProps): React.ReactElement => {
  const { sessions } = props;

  const data = React.useMemo((): IDataPoint[] => {
    return orderBy(
      sessions,
      (session: ISessionMean): number => session.timestamp
    ).reduce((acc: IDataPoint[], session: ISessionMean): IDataPoint[] => {
      acc.unshift({
        Date: session.timestamp,
        Weight: roundValue(session.value, 1),
        units: session.units,
      });
      return acc;
    }, []);
  }, [JSON.stringify(sessions)]);

  return (
    <React.Fragment>
      <div
        style={{
          height: '80mm',
          width: '100%',
        }}
        className='graph-parent'
      >
        <AutoSizer>
          {(size: ISize): React.ReactElement => {
            const tickFormatter = (tick: number): string =>
              tick > 12875256000
                ? dayjs.utc(Number(tick)).format('M/D/YY')
                : tick.toString();

            const shape = (e: {
              cx: number;
              cy: number;
              fill: string;
              height: number;
              width: number;
              y: number;
              x: number;
            }): React.ReactElement => {
              return (
                <circle
                  style={{ cursor: 'pointer' }}
                  cx={e.cx}
                  cy={e.cy}
                  fill={e.fill}
                  height={e.height}
                  width={e.width}
                  y={e.y}
                  x={e.x}
                  r={4}
                />
              );
            };

            return (
              <ScatterChart
                width={size.width}
                height={size.height}
                margin={{ top: 2, right: 20, bottom: 2, left: 0 }}
              >
                <CartesianGrid stroke='#ccc' strokeWidth={0.25} />
                <XAxis
                  type='number'
                  scale='time'
                  dataKey='Date'
                  domain={['auto', 'auto']}
                  tickFormatter={tickFormatter}
                  interval={'preserveStartEnd'}
                  tick={{
                    fill:
                      props.pdfMode === EPdfMode.PRINT
                        ? 'var(--color-text-dark)'
                        : 'var(--color-text)',
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type='number'
                  dataKey='Weight'
                  domain={['auto', 'auto']}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill:
                      props.pdfMode === EPdfMode.PRINT
                        ? 'var(--color-text-dark)'
                        : 'var(--color-text)',
                  }}
                />

                <Scatter
                  name='Session Averages'
                  data={data}
                  fill={
                    props.pdfMode === EPdfMode.PRINT
                      ? 'var(--color-scatter-primary-inverse)'
                      : 'var(--color-scatter-primary)'
                  }
                  line={{
                    stroke:
                      props.pdfMode === EPdfMode.PRINT
                        ? 'var(--color-dark-text)'
                        : 'var(--color-text)',
                    strokeWidth: 2,
                  }}
                  isAnimationActive={false}
                  lineType='fitting'
                  shape={shape}
                />
                <Tooltip
                  content={WeightTooltip}
                  formatter={tickFormatter}
                  cursor={{ strokeDasharray: '3 3' }}
                />
                <Legend
                  formatter={(value) => legendFormatter(value, props.pdfMode)}
                />
              </ScatterChart>
            );
          }}
        </AutoSizer>
      </div>
    </React.Fragment>
  );
};
